import { Component, OnDestroy, OnInit } from '@angular/core';
import { INavbarLink } from '@shared/models/interfaces/navbar-link.interface';
import { environment } from '@env';
import { ToastrService } from 'ngx-toastr';
import { catchError, filter, forkJoin, map, Observable, of, Subscription, switchMap, take } from 'rxjs';
import { Store } from '@ngrx/store';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { cloneDeep } from 'lodash-es';
import { Revisione53Service } from '../../revisione53.service';
import { Revisione53State } from 'app/features/revisione53/core/store/revisione53.reducers';
import { Revisione53CoreSelectors } from 'app/features/revisione53/core/store/selectors/core.selectors';
import { IRevisione, MyVerbale } from 'app/features/revisione53/core/models/interfaces/revisione.interface';
import { Revisione53CoreActions } from '../../store/revisione53.actions';
import { FormOrchestratorService } from '../../form-service-orchestrator/form-orchestrator.service';
import {
    collectIndexedDbIds,
    encodeFileToInfoPath,
    encodeRawBase64ToInfoPath,
    fillAttachmentPlaceholders,
    fillVerbaleTemplate,
    isInfoPathBase64
} from '../../helpers/verbale-to-xml.helper';
import { ATTACHMENT_PLACEHOLDER_B64 } from '../../helpers/verbale-attachment-placeholder';
import {
    BILANCIO_ALLEGATO_PATH,
    isBilancioAllegatoPath,
    setNestedValue
} from '../../helpers/infopath-attachment.constants';
import { IndexedDBService } from '@core/services/indexeddb.service';
import { FormSettlingService } from '../../form-service-orchestrator/form-settling.service';
import { ModalService } from '@core/services/modal.service';
import { CURRENT_INFOPATH_SOLUTION } from '@shared/helpers/infopath.helper';

const IMPORT_MODAL_ID = 'modaleImportXml';
const UPDATE_ERROR_MODAL_ID = 'modaleErroreAggiornamento';
const TEMPLATE_XML_URL = 'assets/import-export/verbale-template.xml';
const BILANCIO_FILE_NAME = 'Bilancio.xbrl';

@Component({
    selector: 'revisione53-main-container',
    templateUrl: './revisione53-main-container.component.html',
    styleUrls: ['./revisione53-main-container.component.scss'],
})
export class Revisione53MainContainerComponent implements OnInit, OnDestroy {
    readonly navbarLinksRilevazione: INavbarLink[];
    readonly navbarLinksAccertamento: INavbarLink[];
    readonly navbarLinksSchedaSettore: INavbarLink[];

    id_assegnazione: string;
    ragione_sociale: string;

    readonly testingMode = environment.testingMode;

    readonly settling$: Observable<boolean>;
    readonly importModalId = IMPORT_MODAL_ID;
    readonly updateErrorModalId = UPDATE_ERROR_MODAL_ID;

    importedVersion = '';
    isOldVersion = false;

    private readonly _subs = new Subscription();

    constructor(
        private revisioneService: Revisione53Service,
        private store: Store<Revisione53State>,
        private router: Router,
        private http: HttpClient,
        private toastr: ToastrService,
        private formOrchestratorService: FormOrchestratorService,
        private indexedDbService: IndexedDBService,
        private settlingService: FormSettlingService,
        private modalService: ModalService
    ) {
        this.navbarLinksRilevazione = this.revisioneService.navbarLinksRilevazione;
        this.navbarLinksAccertamento = this.revisioneService.navbarLinksAccertamento;
        this.navbarLinksSchedaSettore = this.revisioneService.navbarLinksSchedaSettore;
        this.settling$ = this.settlingService.settling$;
    }

    ngOnInit(): void {
        this.id_assegnazione = sessionStorage.getItem('verbale_idAssegnazione');
        this.ragione_sociale = sessionStorage.getItem('verbale_RagioneSociale');
        this._subs.add(
            this.store
                .select(Revisione53CoreSelectors.getUpdateError)
                .pipe(filter(Boolean), take(1))
                .subscribe(() => this.modalService.open(UPDATE_ERROR_MODAL_ID))
        );
    }

    ngOnDestroy(): void {
        this._subs.unsubscribe();
    }

    importVerbale(event: Event): void {
        const input = event.target as HTMLInputElement;

        const file = input.files?.[0];

        if (!file) {
            return;
        }

        this.formOrchestratorService
            .importXmlAndRefresh(file)
            .pipe(take(1))
            .subscribe({
                next: (solutionName) => {
                    this.importedVersion = /V\d+/.exec(solutionName)?.[0] ?? '';
                    this.isOldVersion = solutionName !== CURRENT_INFOPATH_SOLUTION;
                    this.modalService.open(IMPORT_MODAL_ID);
                },
                error: () => this.toastr.error('XML non valido'),
            });
        input.value = '';
    }

    export(): void {
        // Flush any pending (debounced) form edits to the store before reading it, so the exported XML is never stale.
        this.formOrchestratorService.flushAllToStore();
        this._exportVerbale();
    }

    navigationWithArrows(isNext: boolean): void {
        const mainSectionPath = this.router.url.split('/')[2];
        const getItemFromLinkList = (links: INavbarLink[]) => {
            const targetIndex = links.findIndex((l) => l.path === mainSectionPath);
            if (links?.length < 2 || targetIndex < 0) {
                return this.navbarLinksRilevazione[0];
            }
            const targetLink = isNext ? links[targetIndex + 1] : links[targetIndex - 1];
            return targetLink ?? null;
        };
        const newSection =
            getItemFromLinkList(this.navbarLinksRilevazione) || getItemFromLinkList(this.navbarLinksSchedaSettore) || getItemFromLinkList(this.navbarLinksAccertamento);
        this.router.navigate(['revisione53', newSection.path]).then(() => {
            window.scrollTo({
                top: 0,
                left: 0,
                behavior: 'smooth',
            });
        });
    }

    goToFascicolo(): void {
        this.store
            .select(Revisione53CoreSelectors.getRevisione)
            .pipe(take(1))
            .subscribe((revisione: IRevisione) => {
                const revisore = revisione.datiSecondari.aE_Revisore.map((res) => res.cognome + ' ' + res.nome)[0];
                const incaricoPec = revisione.datiSecondari.aE_Incarico.map((res) => res.peCcci)[0];
                const incaricoScadenza = revisione.datiSecondari.aE_Incarico.map((res) => res.scadenza_interna)[0];
                const datiIdentificativi = revisione.dati.my_Verbale.my_SezioneI__Rilevazione.my_DatiIdentificativi;
                const verbale = {
                    id_assegnazione: parseInt(this.id_assegnazione),
                    coop_cap_sa: datiIdentificativi.my_CapSedeAmministrativa,
                    coop_cap_sl: datiIdentificativi.my_CapSedeLegale,
                    coop_cap_sr: datiIdentificativi.my_CapSedeRecapito,
                    coop_cf_modificabile: datiIdentificativi.my_CodFisc,
                    coop_comune_sa: datiIdentificativi.my_ComuneSedeAmministrativa,
                    coop_comune_sl: datiIdentificativi.my_ComuneSedeLegale,
                    coop_comune_sr: datiIdentificativi.my_ComuneSedeRecapito,
                    coop_email_sa: datiIdentificativi.my_SpecificaMailSedeAmministrativa,
                    coop_email_sl: datiIdentificativi.my_SpecificaMailSedeLegale,
                    coop_email_sr: datiIdentificativi.my_SpecificaMailSedeRecapito,
                    coop_fax_sa: datiIdentificativi.my_SpecificaFaxSedeAmministrativa,
                    coop_fax_sl: datiIdentificativi.my_SpecificaFaxSedeLegale,
                    coop_fax_sr: datiIdentificativi.my_SpecificaFaxSedeRecapito,
                    coop_indirizzo_pec: incaricoPec,
                    coop_indirizzo_sa: datiIdentificativi.my_IndirizzoSedeAmministrativa,
                    coop_indirizzo_sl: datiIdentificativi.my_IndirizzoSedeLegale,
                    coop_indirizzo_sr: datiIdentificativi.my_IndirizzoSedeRecapito,
                    coop_matricola: datiIdentificativi.my_Matricola,
                    coop_NumIscrizREA: datiIdentificativi.my_NumREA,
                    coop_p_iva: datiIdentificativi.my_PartitaIVA,
                    coop_provincia_sa: datiIdentificativi.my_ProvinciaSedeAmministrativa,
                    coop_provincia_sl: datiIdentificativi.my_ProvinciaSedeLegale,
                    coop_provincia_sr: datiIdentificativi.my_ProvinciaSedeRecapito,
                    coop_ragione_sociale: datiIdentificativi.my_DenominazEnte,
                    coop_sito_web_sa: datiIdentificativi.my_SpecificaSitoWebSedeAmministrativa,
                    coop_sito_web_sl: datiIdentificativi.my_SpecificaSitoWebSedeLegale,
                    coop_sito_web_sr: datiIdentificativi.my_SpecificaSitoWebSedeRecapito,
                    coop_telefono_sa: datiIdentificativi.my_SpecificaTelefonoSedeAmministrativa,
                    coop_telefono_sl: datiIdentificativi.my_SpecificaTelefonoSedeLegale,
                    coop_telefono_sr: datiIdentificativi.my_SpecificaTelefonoSedeRecapito,
                    incarico: datiIdentificativi.my_Anno1 + '/' + datiIdentificativi.my_Anno2 + ' ' + datiIdentificativi.my_TipoRevisione,
                    incarico_scadenza: incaricoScadenza,
                    revisore_nominativo: revisore,
                };

                this.router.navigateByUrl('prese-in-carico/fascicolo-revisione', { state: verbale });
            });
    }

    getVerbalePrec(): void {
        const id = +this.id_assegnazione;
        const options = { headers: { showSpinner: String(true) } };
        this.http
            .post<any>(`${environment.apiBaseUrl}/api/services/app/AssignmentAppCore/GetDocsInFolder`, id, options)
            .pipe(map((res) => res.result))
            .subscribe((res) => {
                try {
                    const doc = res.filter((r) => r.id_tipo_documento === 4 || r.id_tipo_documento === 5);
                    const link = document.createElement('a');
                    link.href = 'data:application/octet-stream;base64,' + doc[0].file;
                    link.download = doc[0].nome_file;
                    link.click();
                } catch (e) {
                    const err = res?.errore ? 'Errore: ' + res.errore : 'Si è verificato un errore nella ricerca del verbale.';
                    this.toastr.error(err);
                }
            });
    }

    private _xmlToBase64Call(xmlBase64: string): void {
        this.revisioneService.xmlToJson(xmlBase64, true).subscribe((importedVerbale: MyVerbale) => {
            this.store.dispatch(Revisione53CoreActions.updateMyVerbale({ my_Verbale: importedVerbale }));
        });
    }

    /**
     * Export pipeline:
     *   1. read the current MyVerbale from the store + fetch the InfoPath XML template
     *   2. resolve every IndexedDB attachment ID into an InfoPath base64 payload
     *      (real file for Bilancio, placeholder for everything else)
     *   3. convert any raw base64 Bilancio (e.g. from the API) into the InfoPath format
     *   4. defensively fill remaining non-empty attachment fields with the placeholder
     *   5. fill the template and trigger the download
     */
    private _exportVerbale(): void {
        this.store
            .select(Revisione53CoreSelectors.getMyVerbale)
            .pipe(
                take(1),
                switchMap((verbale) => this._loadTemplate().pipe(map((templateXml) => ({ verbale, templateXml })))),
                switchMap(({ verbale, templateXml }) => {
                    const verbaleForExport = cloneDeep(verbale) as Record<string, unknown>;
                    return this._resolveAttachments(verbaleForExport).pipe(map(() => ({ verbale, verbaleForExport, templateXml })));
                })
            )
            .subscribe({
                next: ({ verbale, verbaleForExport, templateXml }) => {
                    this._encodeBilancioRawBase64(verbaleForExport);
                    fillAttachmentPlaceholders(verbaleForExport, ATTACHMENT_PLACEHOLDER_B64);
                    const xml = fillVerbaleTemplate(templateXml, verbaleForExport as unknown as MyVerbale);
                    this._downloadXml(verbale, xml);
                },
                error: () => this.toastr.error('Errore durante la generazione del file XML.'),
            });
    }

    /** Loads the blank InfoPath XML template from the app assets. */
    private _loadTemplate(): Observable<string> {
        return this.http.get(TEMPLATE_XML_URL, { responseType: 'text' });
    }

    /**
     * For every IndexedDB ID in `verbaleForExport`, replaces the ID in place with:
     *   - the InfoPath-encoded real file for the Bilancio attachment (with placeholder fallback);
     *   - the placeholder InfoPath payload for any other attachment.
     */
    private _resolveAttachments(verbaleForExport: Record<string, unknown>): Observable<void> {
        const allIds = collectIndexedDbIds(verbaleForExport);
        if (allIds.length === 0) return of(undefined);

        const encodes$ = allIds.map(({ path, id }) => this._encodeAttachment(path, id).pipe(map((b64) => ({ path, b64 }))));

        return forkJoin(encodes$).pipe(
            map((results) => {
                for (const { path, b64 } of results) {
                    setNestedValue(verbaleForExport, path, b64);
                }
            })
        );
    }

    /** Resolves a single IndexedDB attachment ID to its InfoPath base64 payload. */
    private _encodeAttachment(path: string[], id: string): Observable<string> {
        if (!isBilancioAllegatoPath(path)) {
            return of(ATTACHMENT_PLACEHOLDER_B64);
        }
        return this.indexedDbService.getByID(id).pipe(
            switchMap((allegato) => (allegato?.file ? encodeFileToInfoPath(allegato.file) : of(ATTACHMENT_PLACEHOLDER_B64))),
            catchError(() => of(ATTACHMENT_PLACEHOLDER_B64))
        );
    }

    /** Converts a raw base64 Bilancio (from UI upload or API) into InfoPath format, in place. */
    private _encodeBilancioRawBase64(verbaleForExport: Record<string, unknown>): void {
        let parent = verbaleForExport;
        for (const key of BILANCIO_ALLEGATO_PATH.slice(0, -1)) {
            parent = parent[key] as Record<string, unknown>;
        }
        const leafKey = BILANCIO_ALLEGATO_PATH[BILANCIO_ALLEGATO_PATH.length - 1];
        const value = parent[leafKey] as string;
        if (value && !isInfoPathBase64(value)) {
            parent[leafKey] = encodeRawBase64ToInfoPath(value, BILANCIO_FILE_NAME);
        }
    }

    /** Triggers a browser download of the generated XML, named after the verbale matricola. */
    private _downloadXml(verbale: MyVerbale, xml: string): void {
        const matricola = verbale.my_SezioneI__Rilevazione?.my_DatiIdentificativi?.my_Matricola ?? 'verbale';
        const blob = new Blob([xml], { type: 'application/xml;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${matricola}_export_verb.xml`;
        link.click();
        URL.revokeObjectURL(url);
    }
}
